'use client';

import { useMemo, useRef, useState } from 'react';
import {
  COOKING_UNITS,
  INGREDIENTS,
  convertCooking,
  kitchenFraction,
  type CookingUnit,
} from '@/lib/browser-cooking';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const SINGULAR: Record<CookingUnit, string> = {
  g: 'gram',
  kg: 'kilogram',
  oz: 'ounce',
  lb: 'pound',
  ml: 'milliliter',
  cup: 'cup (US)',
  tbsp: 'tablespoon',
  tsp: 'teaspoon',
};
const unitName = (id: CookingUnit, n: number) =>
  n === 1 ? SINGULAR[id] : (COOKING_UNITS.find((u) => u.id === id)?.label ?? id);
const isUnit = (v: string | undefined): v is CookingUnit => COOKING_UNITS.some((u) => u.id === v);
const fmt = (v: number) =>
  Number.isFinite(v) ? v.toLocaleString('en-US', { maximumFractionDigits: v < 10 ? 3 : 1 }) : '';

export default function BrowserCookingConverter({
  config,
}: {
  config?: {
    toolSlug?: string;
    heading?: string;
    variant?: string;
    defaultUnitFrom?: string;
    defaultUnitTo?: string;
    defaultUnitValue?: number;
  };
}) {
  const [amount, setAmount] = useState(String(config?.defaultUnitValue ?? 100));
  const [from, setFrom] = useState<CookingUnit>(
    isUnit(config?.defaultUnitFrom) ? config.defaultUnitFrom : 'g',
  );
  const [to, setTo] = useState<CookingUnit>(
    isUnit(config?.defaultUnitTo) ? config.defaultUnitTo : 'cup',
  );
  const [ingredient, setIngredient] = useState(
    INGREDIENTS.some((i) => i.id === config?.variant) ? (config?.variant as string) : 'flour',
  );
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'grams-to-cups', '').catch(() => undefined);
  };

  const density = INGREDIENTS.find((i) => i.id === ingredient)?.gramsPerCup ?? 120;
  const value = Number(amount);
  const result = convertCooking(value, from, to, density);
  const toVolume = COOKING_UNITS.find((u) => u.id === to)?.kind === 'volume';
  const nice = toVolume && to !== 'ml' ? kitchenFraction(result) : null;
  const fromLabel = unitName(from, value);
  const toLabel = unitName(to, result);

  const table = useMemo(
    () =>
      INGREDIENTS.map((i) => ({
        name: i.name,
        value: convertCooking(Number.isFinite(value) ? value : 0, from, to, i.gramsPerCup),
      })),
    [value, from, to],
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Kitchen</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Grams to cups converter'}</h2>
        <p className="tool-live-subtitle">
          Weight and volume only convert for a specific ingredient, so pick it first. US cups, flour
          spooned and leveled.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Ingredient</span>
            <select
              onChange={(e) => {
                report();
                setIngredient(e.target.value);
              }}
              value={ingredient}
            >
              {INGREDIENTS.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>Amount</span>
            <input
              inputMode="decimal"
              onChange={(e) => {
                report();
                setAmount(e.target.value);
              }}
              type="text"
              value={amount}
            />
          </label>
          <label className="tool-live-field">
            <span>From</span>
            <select
              onChange={(e) => {
                report();
                setFrom(e.target.value as CookingUnit);
              }}
              value={from}
            >
              {COOKING_UNITS.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
          <label className="tool-live-field">
            <span>To</span>
            <select
              onChange={(e) => {
                report();
                setTo(e.target.value as CookingUnit);
              }}
              value={to}
            >
              {COOKING_UNITS.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
          </label>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => {
                report();
                setFrom(to);
                setTo(from);
              }}
              type="button"
            >
              Swap units
            </button>
          </div>
        </section>
        <section className="tool-live-preview">
          {Number.isFinite(value) && amount.trim() ? (
            <div className="tool-live-stats">
              <p className="tool-live-title">
                {fmt(result)} {toLabel}
              </p>
              <p className="tool-live-subtitle">
                {amount} {fromLabel} of{' '}
                {INGREDIENTS.find((i) => i.id === ingredient)?.name.toLowerCase()}
                {nice
                  ? `, about ${nice} ${to === 'cup' ? (nice === '1' ? 'cup' : 'cups') : to}`
                  : ''}
              </p>
            </div>
          ) : (
            <p className="status-message status-error">Enter an amount, like 100 or 1.5.</p>
          )}
          <table
            className="tool-live-table"
            style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}
          >
            <caption style={{ textAlign: 'left', padding: '0.5rem 0' }}>
              {amount} {fromLabel} in {toLabel}, by ingredient
            </caption>
            <tbody>
              {table.map((row) => (
                <tr key={row.name}>
                  <th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>
                    {row.name}
                  </th>
                  <td style={{ textAlign: 'right' }}>{fmt(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
